import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RiskAssessmentService } from "@root/services/risk-assessment";
import { useNotification } from "./useNotification";
import { PAGINATION_DEFAULTS } from "@root/constants";
import { ChatService } from "@root/services";

function useRiskAssessment() {
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");
  const riskAssessmentService = new RiskAssessmentService();

  const [riskAssessmentLoading, setRiskAssessmentLoading] = useState(false);
  const [addRiskAssessmentLoading, setAddRiskAssessmentLoading] =
    useState(false);
  const [deleteRiskAssessmentLoading, setDeleteRiskAssessmentLoading] =
    useState(false);
  const [updateRiskAssessmentLoading, setUpdateRiskAssessmentLoading] =
    useState(false);
  const [riskAssessmentCount, setRiskAssessmentCount] = useState(0);
  const [riskAssessmentData, setRiskAssessmentData] = useState<any>(null);
  const [pageNo, setPageNo] = useState(1);
  const [search, setSearch] = useState("");
  const [pageCount, setPageCount] = useState(0);
  const [newCategory, setNewCategory] = useState("");
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [addNewCategory, setAddNewCategory] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  // Suggested Questions state
  const [suggestedQuestionsLoading, setSuggestedQuestionsLoading] =
    useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<any[]>([]);

  // Shared AI model selection state
  const [selectedAskAiModel, setSelectedAskAiModel] = useState<{
    id: number;
    labelKey: string;
  } | null>(null);
  const [selectedMultiAiModel, setSelectedMultiAiModel] = useState<{
    id: number;
    labelKey: string;
  } | null>(null);

  const payload = {
    limit: PAGINATION_DEFAULTS.PAGESIZE,
    offset: (pageNo - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    search: search,
  };

  const addRiskAssessment = async (payload: any) => {
    setAddRiskAssessmentLoading(true);
    try {
      const response = await riskAssessmentService.addRiskAssessment(payload);
      if (response?.data) {
        showNotification({
          message: t("assessments.notifications.addSuccess"),
          type: "success",
        });
        return response?.data;
      }
    } catch (error: any) {
      showNotification({
        message: t("assessments.notifications.addError"),
        type: "error",
      });
      throw error;
    } finally {
      setAddRiskAssessmentLoading(false);
    }
  };

  const fetchRiskAssessments = async (payload: any) => {
    setRiskAssessmentLoading(true);
    try {
      const response = await riskAssessmentService.fetchRiskAssessments(
        payload
      );
      if (response?.data) {
        setRiskAssessmentData(response?.data?.data?.rows);
        setRiskAssessmentCount(response?.data?.data?.count || 0);
        return response?.data;
      }
    } catch (error: any) {
      showNotification({
        message: t("assessments.notifications.fetchError"),
        type: "error",
      });
      throw error;
    } finally {
      setRiskAssessmentLoading(false);
    }
  };

  const deleteRiskAssessment = async (id: number) => {
    setDeleteRiskAssessmentLoading(true);
    try {
      const response = await riskAssessmentService.deleteRiskAssessment(id);
      if (response?.data) {
        showNotification({
          message: t("assessments.notifications.deleteSuccess"),
          type: "success",
        });
        return response?.data;
      }
    } catch (error: any) {
      showNotification({
        message: t("assessments.notifications.deleteError"),
        type: "error",
      });
      throw error;
    } finally {
      setDeleteRiskAssessmentLoading(false);
    }
  };

  const updateRiskAssessment = async (id: number, payload: any) => {
    setUpdateRiskAssessmentLoading(true);
    try {
      const response = await riskAssessmentService.updateRiskAssessment(
        id,
        payload
      );
      if (response?.data) {
        showNotification({
          message: t("assessments.notifications.updateSuccess"),
          type: "success",
        });
        return response?.data;
      }
    } catch (error: any) {
      showNotification({
        message: t("assessments.notifications.updateError"),
        type: "error",
      });
      throw error;
    } finally {
      setUpdateRiskAssessmentLoading(false);
    }
  };

  // Fetch Suggested Questions using query params
  const fetchSuggestedQuestions = async (params: {
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
    searchQuery?: string;
    userId?: number;
    moduleType?: number; // 1,2,3
    modelType?: number; // 1..5
  }) => {
    setSuggestedQuestionsLoading(true);
    try {
      const response = await riskAssessmentService.fetchSuggestedQuestions(
        params
      );
      const rows = response?.data?.data?.rows || response?.data?.rows || [];
      setSuggestedQuestions(rows);
      return rows;
    } catch (error) {
      showNotification({
        message: t("feedback.somethingWrong"),
        type: "error",
      });
    } finally {
      setSuggestedQuestionsLoading(false);
    }
  };

  const getRiskAssessmentById = async (id: string | number) => {
    setRiskAssessmentLoading(true);
    try {
      const response = await riskAssessmentService.getRiskAssessmentById(id);
      if (response?.data) {
        return response?.data;
      }
    } catch (error: any) {
      showNotification({
        message: t("assessments.notifications.fetchError"),
        type: "error",
      });
      throw error;
    } finally {
      setRiskAssessmentLoading(false);
    }
  };

  return {
    pageCount,
    riskAssessmentCount,
    riskAssessmentLoading,
    addRiskAssessmentLoading,
    deleteRiskAssessmentLoading,
    updateRiskAssessmentLoading,
    fetchRiskAssessments,
    addRiskAssessment,
    updateRiskAssessment,
    deleteRiskAssessment,
    getRiskAssessmentById,
    fetchSuggestedQuestions,
    suggestedQuestionsLoading,
    suggestedQuestions,
    // shared model states
    selectedAskAiModel,
    setSelectedAskAiModel,
    selectedMultiAiModel,
    setSelectedMultiAiModel,
    riskAssessmentData,
    setRiskAssessmentData,
    search,
    setSearch,
    newCategory,
    setNewCategory,
    pageNo,
    setPageNo,
    hoveredId,
    setHoveredId,
    addNewCategory,
    setAddNewCategory,
    editingId,
    setEditingId,
    editingValue,
    setEditingValue,
  };
}

/**
 * Fetches PDF URL from a source item (standalone export)
 * @param sourceItem - The source item containing file_name and folder_name
 * @param baseAuthUrl - The base authentication URL
 * @param chatService - Instance of ChatService
 * @returns Promise<string | null> - The PDF URL or null if failed
 */
export const fetchPdfUrl = async (
  sourceItem: any,
  baseAuthUrl: string,
  chatService: ChatService
): Promise<string | null> => {
  try {
    let name = sourceItem?.file_name?.split("_");
    let file_path = sourceItem?.folder_name + "/" + name[name?.length - 1];
    // Make API call using service to get file URL
    const response = await chatService.getChatFilePath(baseAuthUrl, {
      filePath: file_path,
    });

    return response?.data?.data?.fileUrl || sourceItem?.signedUrl || null;
  } catch (error) {
    console.error("Error fetching file:", error);
    // Fallback to direct signed URL if API call fails
    return sourceItem?.signedUrl || null;
  }
};

export default useRiskAssessment;
